import {
	existsSync,
	mkdirSync,
	readFileSync,
	chmodSync,
	openSync,
	writeSync,
	closeSync,
	renameSync,
	unlinkSync,
} from 'node:fs';
import { dirname, join } from 'node:path';
import envPaths from 'env-paths';
import { CliError, ExitCode } from './errors';
import { printWarn } from './output';

export const ALLOWED_LOCALES = ['zh-TW', 'en-US', 'vi-VN', 'th-TH', 'id-ID'] as const;
export type Locale = (typeof ALLOWED_LOCALES)[number];

export const ALLOWED_OUTPUT = ['table', 'json'] as const;
export type OutputPref = (typeof ALLOWED_OUTPUT)[number];

export interface CliConfig {
	locale?: Locale;
	output?: OutputPref;
	timeout_ms?: number;
}

const CONFIG_KEYS = ['locale', 'output', 'timeout_ms'] as const satisfies readonly (keyof CliConfig)[];
export type ConfigKey = (typeof CONFIG_KEYS)[number];

/**
 * Keys recognised in older configs but no longer settable. Detected in parseConfig
 * to surface a one-time deprecation warning instead of silently dropping (which the
 * forward-compat path would do for genuinely unknown keys).
 *
 * `api_base_url` was removed in 0.1.2 — SSRF / credential exfil surface for a flag
 * external users don't actually need. Override via `WPORT_API_BASE` env var or `--api`.
 */
const DEPRECATED_CONFIG_KEYS = ['api_base_url'] as const;
type DeprecatedConfigKey = (typeof DEPRECATED_CONFIG_KEYS)[number];

/** Per-key migration hint shown when a deprecated key is found in an existing config. */
const DEPRECATED_KEY_HINTS: Record<DeprecatedConfigKey, string> = {
	api_base_url: 'Set the WPORT_API_BASE env var (or use --api) instead.',
};

// One-time latch so repeated loadConfig() calls (e.g. inside a batch run) emit the
// deprecation notice at most once per process rather than spamming stderr.
let deprecationWarned = false;

/** Per-key value type. validateAndCoerce<K> returns ConfigValueMap[K]. */
type ConfigValueMap = {
	locale: Locale;
	output: OutputPref;
	timeout_ms: number;
};

export function isConfigKey(key: string): key is ConfigKey {
	return (CONFIG_KEYS as readonly string[]).includes(key);
}

export function isDeprecatedConfigKey(key: string): key is DeprecatedConfigKey {
	return (DEPRECATED_CONFIG_KEYS as readonly string[]).includes(key);
}

const paths = envPaths('wport', { suffix: '' });

export function getConfigPath(): string {
	return join(paths.config, 'config.json');
}

/**
 * Trust boundary：把外部 JSON object 收成型別正確的 CliConfig。
 * 每個 key 都走 validateAndCoerce，不認識的 key 直接 drop（forward compatible，
 * 未來新版多塞了 key、舊 CLI 不會炸）。
 */
export function parseConfig(raw: unknown): CliConfig {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
		throw new CliError('Config must be a JSON object', ExitCode.ConfigCorrupt);
	}
	const input = raw as Record<string, unknown>;

	// Surface (once per process) any deprecated key still sitting in the user's config.
	// We warn + drop rather than error, so upgrading from <=0.1.1 never hard-fails; the
	// key's actual replacement (WPORT_API_BASE) is resolved elsewhere in global-opts.
	if (!deprecationWarned) {
		for (const dep of DEPRECATED_CONFIG_KEYS) {
			if (dep in input) {
				deprecationWarned = true;
				printWarn(`Config key "${dep}" was removed in 0.1.2 and is ignored. ${DEPRECATED_KEY_HINTS[dep]}`, false);
			}
		}
	}

	const out: CliConfig = {};
	for (const key of CONFIG_KEYS) {
		if (!(key in input)) continue;
		const value = input[key];
		try {
			const coerced = validateAndCoerce(key, String(value));
			// Object.assign 形式避免 TS 5.5 在 `out[key] = coerced` 上推不出 ConfigValueMap[K]→CliConfig[K]
			// 的對應關係。validateAndCoerce 已是 generic、型別正確；此處只是 indexed assignment 的繞道。
			Object.assign(out, { [key]: coerced });
		} catch (err) {
			if (err instanceof CliError) {
				throw new CliError(`Config key "${key}" invalid: ${err.message}`, ExitCode.ConfigCorrupt);
			}
			throw err;
		}
	}
	return out;
}

export function loadConfig(): CliConfig {
	const path = getConfigPath();
	if (!existsSync(path)) return {};
	let raw: string;
	try {
		raw = readFileSync(path, 'utf8');
	} catch (err) {
		throw new CliError(`Failed to read config at ${path}: ${(err as Error).message}`, ExitCode.ConfigCorrupt);
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch (err) {
		throw new CliError(`Failed to parse JSON at ${path}: ${(err as Error).message}`, ExitCode.ConfigCorrupt);
	}
	return parseConfig(parsed);
}

export function saveConfig(config: CliConfig): void {
	const path = getConfigPath();
	mkdirSync(dirname(path), { recursive: true });

	// Atomic: write tmpfile (mode 0o600 from open()) → rename to final path (POSIX atomic).
	// 即使中途 crash，舊檔仍完整、不會留 truncated JSON 觸發 ConfigCorrupt 鎖死 CLI。
	const tmpPath = `${path}.tmp.${process.pid}.${Date.now()}`;
	const fd = openSync(tmpPath, 'w', 0o600);
	try {
		writeSync(fd, JSON.stringify(config, null, 2) + '\n');
	} catch (err) {
		closeSync(fd);
		try {
			unlinkSync(tmpPath);
		} catch {
			/* best effort cleanup */
		}
		throw err;
	}
	closeSync(fd);

	// POSIX: openSync's mode is masked by umask (umask can only narrow, never widen).
	// chmod restores 0o600 in case a permissive umask stripped owner bits; it cannot
	// expose the file to group/other. On Windows chmodSync is a no-op, skip entirely.
	if (process.platform !== 'win32') {
		try {
			chmodSync(tmpPath, 0o600);
		} catch (err) {
			printWarn(
				`Failed to chmod 0600 on config tmpfile: ${(err as Error).message}. ` +
					'Other users on this system may be able to read CLI config.',
				false
			);
		}
	}

	renameSync(tmpPath, path);
}

export function validateAndCoerce<K extends ConfigKey>(key: K, value: string): ConfigValueMap[K] {
	switch (key) {
		case 'locale': {
			if (!(ALLOWED_LOCALES as readonly string[]).includes(value)) {
				throw new CliError(
					`Invalid locale "${value}". Allowed: ${ALLOWED_LOCALES.join(', ')}`,
					ExitCode.InvalidArgument
				);
			}
			return value as ConfigValueMap[K];
		}
		case 'output': {
			if (!(ALLOWED_OUTPUT as readonly string[]).includes(value)) {
				throw new CliError(
					`Invalid output "${value}". Allowed: ${ALLOWED_OUTPUT.join(', ')}`,
					ExitCode.InvalidArgument
				);
			}
			return value as ConfigValueMap[K];
		}
		case 'timeout_ms': {
			const n = Number(value);
			if (!Number.isInteger(n) || n < 100 || n > 600_000) {
				throw new CliError(
					`timeout_ms must be an integer between 100 and 600000 (got ${value})`,
					ExitCode.InvalidArgument
				);
			}
			return n as ConfigValueMap[K];
		}
	}
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = {
	resetDeprecationWarning(): void {
		deprecationWarned = false;
	},
};
